import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { LanguageContext } from '../contexts/LanguageContext';
import { ThemeContext } from '../contexts/ThemeContext';

const CaseStudyCard = ({ title, company, industry, challenge, solution, result, image, slug, delay = 0, readCaseStudyText }) => {
  const { theme } = useContext(ThemeContext);
  return (
    <div 
      className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 group"
      data-aos="fade-up"
      data-aos-delay={delay}
    >
      <div className="h-56 bg-gray-200 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent opacity-80 z-10"></div>
        
        {/* Background design elements */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-blue-500/20 to-teal-500/30 rounded-full -mr-16 -mt-16 transform group-hover:scale-110 transition-transform duration-500"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-indigo-500/20 to-blue-500/30 rounded-full -ml-10 -mb-10 transform group-hover:scale-110 transition-transform duration-500"></div>
        
        {/* Content */}
        <div className="absolute bottom-0 left-0 p-6 text-white z-20 w-full transform transition-transform duration-500 group-hover:translate-y-[-8px]">
          <div className="bg-gradient-to-r from-blue-600/80 to-teal-500/80 inline-block py-1 px-3 rounded-full mb-2">
            <p className="text-sm uppercase tracking-wider font-medium">{industry}</p>
          </div>
          <h3 className="text-2xl font-bold">{company}</h3>
        </div>
      </div>
      <div className="p-6 dark:bg-gray-800">
        <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">{title}</h4>
        <p className="text-gray-700 dark:text-gray-300 mb-5 line-clamp-3">{challenge}</p>
        <div className="transform transition-transform duration-300 group-hover:translate-x-2">
          <Link 
            to={`/customer-stories/${slug}`} 
            className="inline-flex items-center font-bold text-blue-600 hover:text-blue-800 transition-colors"
          >
            <span>{readCaseStudyText}</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

const CustomerStories = () => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
      easing: 'ease-out-cubic'
    });
    
    window.scrollTo(0, 0);
    
    return () => {
      AOS.refresh();
    };
  }, []);
  const caseStudies = [
    {
      title: translations['customerStories.caseStudy1.title'],
      company: translations['customerStories.caseStudy1.company'],
      industry: translations['customerStories.caseStudy1.industry'],
      challenge: translations['customerStories.caseStudy1.challenge'],
      solution: translations['customerStories.caseStudy1.solution'],
      result: translations['customerStories.caseStudy1.result'],
      image: "/images/case-study-1.jpg",
      slug: "fintech-solutions"
    },
    {
      title: translations['customerStories.caseStudy2.title'],
      company: translations['customerStories.caseStudy2.company'],
      industry: translations['customerStories.caseStudy2.industry'],
      challenge: translations['customerStories.caseStudy2.challenge'],
      solution: translations['customerStories.caseStudy2.solution'],
      result: translations['customerStories.caseStudy2.result'],
      image: "/images/case-study-2.jpg",
      slug: "global-retail-group"
    },
    {
      title: translations['customerStories.caseStudy3.title'],
      company: translations['customerStories.caseStudy3.company'],
      industry: translations['customerStories.caseStudy3.industry'],
      challenge: translations['customerStories.caseStudy3.challenge'],
      solution: translations['customerStories.caseStudy3.solution'],
      result: translations['customerStories.caseStudy3.result'],
      image: "/images/case-study-3.jpg",
      slug: "meditech-systems"
    },
    {
      title: translations['customerStories.caseStudy4.title'],
      company: translations['customerStories.caseStudy4.company'],
      industry: translations['customerStories.caseStudy4.industry'],
      challenge: translations['customerStories.caseStudy4.challenge'],
      solution: translations['customerStories.caseStudy4.solution'],
      result: translations['customerStories.caseStudy4.result'],
      image: "/images/case-study-4.jpg",
      slug: "techglobal-solutions"
    },
    {
      title: translations['customerStories.caseStudy5.title'],
      company: translations['customerStories.caseStudy5.company'],
      industry: translations['customerStories.caseStudy5.industry'],
      challenge: translations['customerStories.caseStudy5.challenge'],
      solution: translations['customerStories.caseStudy5.solution'],
      result: translations['customerStories.caseStudy5.result'],
      image: "/images/case-study-5.jpg",
      slug: "energygrid-operators"
    },
    {
      title: translations['customerStories.caseStudy6.title'],
      company: translations['customerStories.caseStudy6.company'],
      industry: translations['customerStories.caseStudy6.industry'],
      challenge: translations['customerStories.caseStudy6.challenge'],
      solution: translations['customerStories.caseStudy6.solution'],
      result: translations['customerStories.caseStudy6.result'],
      image: "/images/case-study-6.jpg",
      slug: "innovative-insurance"
    }
  ];

  // Testimonials from the component
  const testimonials = [
    {
      quote: translations['customerStories.testimonial1.quote'],
      author: translations['customerStories.testimonial1.author'],
      position: translations['customerStories.testimonial1.position']
    },
    {
      quote: translations['customerStories.testimonial2.quote'],
      author: translations['customerStories.testimonial2.author'],
      position: translations['customerStories.testimonial2.position']
    },
    {
      quote: translations['customerStories.testimonial3.quote'],
      author: translations['customerStories.testimonial3.author'],
      position: translations['customerStories.testimonial3.position']
    }
  ];

  return (
    <div className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950 min-h-screen">
      <Helmet>
        <title>{translations['customerStories.pageTitle']}</title>
        <meta name="description" content={translations['customerStories.meta.description']} />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h1 className="text-5xl font-extrabold text-gray-800 dark:text-white mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">{translations['customerStories.heroTitle']}</span>
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            {translations['customerStories.heroSubtitle']}
          </p>
        </div>
        
        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center" data-aos="zoom-in" data-aos-delay="0">
            <div className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-400 mb-2">97%</div>
            <p className="text-gray-700 dark:text-gray-300">{translations['customerStories.stats1']}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center" data-aos="zoom-in" data-aos-delay="100">
            <div className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-teal-400 mb-2">60%</div>
            <p className="text-gray-700 dark:text-gray-300">{translations['customerStories.stats2']}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center" data-aos="zoom-in" data-aos-delay="200">
            <div className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-400 mb-2">40%</div>
            <p className="text-gray-700 dark:text-gray-300">{translations['customerStories.stats3']}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg text-center" data-aos="zoom-in" data-aos-delay="300">
            <div className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-purple-400 mb-2">99.9%</div>
            <p className="text-gray-700 dark:text-gray-300">{translations['customerStories.stats4']}</p>
          </div>
        </div>

        {/* Featured Case Studies */}
        <div className="mb-24" data-aos="fade-up">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-10 text-center">
              <span className="border-b-4 border-blue-500 dark:border-blue-400 pb-2">{translations['customerStories.featuredCaseStudies']}</span>
            </h2>
            <div className="hidden md:flex space-x-2 mt-4 md:mt-0">
              <span className="w-3 h-3 bg-blue-600 rounded-full"></span>
              <span className="w-3 h-3 bg-teal-500 rounded-full"></span>
              <span className="w-3 h-3 bg-indigo-600 rounded-full"></span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <CaseStudyCard 
                key={index}
                title={study.title}
                company={study.company}
                industry={study.industry}
                challenge={study.challenge}
                solution={study.solution}
                result={study.result}
                image={study.image}
                slug={study.slug}
                delay={index * 100}
                readCaseStudyText={translations['customerStories.readCaseStudy']}
              />
            ))}
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 p-1 rounded-2xl shadow-xl mb-20" data-aos="fade-up">
          <div className="absolute top-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mt-12 blur-xl"></div>
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mb-16 blur-xl"></div>
          
          <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl">
            <h2 className="text-3xl font-bold text-center mb-10 flex items-center justify-center dark:text-white">
              <span className="mr-3 text-2xl">“</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">{translations['customerStories.clientTestimonials']}</span>
              <span className="ml-3 text-2xl">”</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} data-aos="zoom-in" data-aos-delay={index * 100} className="bg-gray-50 dark:bg-gray-700 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 border-t-4 border-blue-600">
                  <svg className="w-10 h-10 text-gray-200 mb-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z"/>
                  </svg>
                  
                  <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed italic mb-6">"{testimonial.quote}"</p>
                  
                  <div className="border-t pt-4 mt-2">
                    <h4 className="font-bold text-gray-800 dark:text-gray-100 text-lg">{testimonial.author}</h4>
                    <p className="text-blue-600 dark:text-blue-400 font-medium">{testimonial.position}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-12 text-center mb-8 shadow-xl relative overflow-hidden" data-aos="fade-up">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full -ml-32 -mb-32 blur-2xl"></div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">{translations['customerStories.transformCTA']}</h1>
          <p className="text-white text-opacity-90 max-w-2xl mx-auto mb-10 text-lg relative z-10">
            {translations['customerStories.transformSubtext']}
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
            <Link 
              to="/contact-us" 
              className="inline-block bg-white text-blue-600 hover:bg-gray-100 font-bold px-8 py-4 rounded-lg transition transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              {translations['customerStories.scheduleCTA']}
            </Link>
            <Link 
              to="/services" 
              className="inline-block bg-transparent text-white border-2 border-white hover:bg-white/10 font-bold px-8 py-4 rounded-lg transition transform hover:scale-105"
            >
              {translations['customerStories.exploreCTA']}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerStories;
